import React, { useState, useEffect } from 'react';
import { Collapse, IconButton } from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { Box, Card, Typography } from '../../components/primitives';
import EventsList from './EventsList';
import GoogleMapsComponent from './GoogleMapsComponent';
import SearchComponent from './SearchComponent';
import { borderColor, borderRadius } from '@mui/system';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Default center (center of US) if no primary address
const DEFAULT_CENTER = { lat: 42.3663248035888, lng: -71.06166320446663 };

// Helper function to get today's date
const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

// Helper function to get date 30 days from today
const getDate30DaysFromToday = () => {
  const date = new Date();
  date.setDate(date.getDate() + 30);
  date.setHours(0, 0, 0, 0);
  return date;
};

// Helper function to get default start time (5:00 AM)
const getDefaultStartTime = () => {
  const time = new Date();
  time.setHours(5, 0, 0, 0);
  return time;
};

// Helper function to get default end time (11:59 PM)
const getDefaultEndTime = () => {
  const time = new Date();
  time.setHours(23, 59, 0, 0);
  return time;
};

// Helper function to combine date and time into ISO datetime string
const combineDateAndTime = (date, time) => {
  const combined = new Date(date);
  combined.setHours(time.getHours(), time.getMinutes(), 0, 0);
  return combined.toISOString();
};

const HomePage = ({ isGoogleMapsLoaded, googleMapsLoadError }) => {
  const [primaryAddress, setPrimaryAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [facilities, setFacilities] = useState([]);
  const [events, setEvents] = useState([]);
  const [resources, setResources] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(true);
  const [resultsExpanded, setResultsExpanded] = useState(false);
  
  // Search state - lifted from SearchComponent
  const [radius, setRadius] = useState(20);
  const [startDate, setStartDate] = useState(getToday());
  const [startTime, setStartTime] = useState(getDefaultStartTime());
  const [endDate, setEndDate] = useState(getDate30DaysFromToday());
  const [endTime, setEndTime] = useState(getDefaultEndTime());

  useEffect(() => {
    fetchPrimaryAddress();
  }, []);

  // Track if radius was set from address default_radius to avoid auto-search
  const radiusFromAddressRef = React.useRef(false);
  const hasInitializedRef = React.useRef(false);

  // Update radius when selected address changes
  useEffect(() => {
    if (selectedAddress && selectedAddress.default_radius) {
      radiusFromAddressRef.current = true;
      setRadius(selectedAddress.default_radius);
      // Reset flag after state update
      setTimeout(() => {
        radiusFromAddressRef.current = false;
      }, 0);
    }
  }, [selectedAddress]);

  // Mark as initialized after initial load completes
  useEffect(() => {
    if (!loading) {
      hasInitializedRef.current = true;
    }
  }, [loading]);

  // Auto-search when radius changes (but not when set from address default_radius)
  useEffect(() => {
    // Skip on initial load
    if (!hasInitializedRef.current) {
      return;
    }

    // Skip if radius was just set from address default_radius
    if (radiusFromAddressRef.current) {
      return;
    }

    // Trigger search with new radius (handleSearch handles both cases: with/without selectedAddress)
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius]);

  // Handler for search button click
  const handleSearch = async () => {
    if (!selectedAddress || !selectedAddress.latitude || !selectedAddress.longitude) {
      // No selected address - use default center
      const startDateTime = combineDateAndTime(startDate, startTime);
      const endDateTime = combineDateAndTime(endDate, endTime);
      await fetchNearbyFacilities(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng, radius, startDateTime, endDateTime);
    } else {
      const location = {
        lat: parseFloat(selectedAddress.latitude),
        lng: parseFloat(selectedAddress.longitude),
      };
      const startDateTime = combineDateAndTime(startDate, startTime);
      const endDateTime = combineDateAndTime(endDate, endTime);
      await fetchNearbyFacilities(location.lat, location.lng, radius, startDateTime, endDateTime);
    }
  };

  const handleSearchClick = async () => {
    setResultsExpanded(true);
    await handleSearch();
  };

  // Handler for address selection from popup (authenticated users)
  const handleAddressSelect = async (address) => {
    setSelectedAddress(address);
    
    // Update radius from selected address
    if (address.default_radius) {
      setRadius(address.default_radius);
    }
    
    // Update map center if address has coordinates
    if (address.latitude && address.longitude) {
      const location = {
        lat: parseFloat(address.latitude),
        lng: parseFloat(address.longitude),
      };
      setMapCenter(location);
      
      // Trigger search with new location
      const startDateTime = combineDateAndTime(startDate, startTime);
      const endDateTime = combineDateAndTime(endDate, endTime);
      const searchRadius = address.default_radius || radius;
      await fetchNearbyFacilities(location.lat, location.lng, searchRadius, startDateTime, endDateTime);
    }
  };

  // Handler for address change from autocomplete (non-authenticated users)
  const handleAddressChange = async (addressData) => {
    setSelectedAddress(addressData);
    
    // Update map center if address has coordinates
    if (addressData.latitude && addressData.longitude) {
      const location = {
        lat: parseFloat(addressData.latitude),
        lng: parseFloat(addressData.longitude),
      };
      setMapCenter(location);
      
      // Trigger search with new location
      const startDateTime = combineDateAndTime(startDate, startTime);
      const endDateTime = combineDateAndTime(endDate, endTime);
      await fetchNearbyFacilities(location.lat, location.lng, radius, startDateTime, endDateTime);
    }
  };

  const fetchNearbyFacilities = async (latitude, longitude, radius, startDateTime, endDateTime) => {
    try {
      setFacilitiesLoading(true);
      const params = new URLSearchParams({
        latitude: latitude.toString(),
        longitude: longitude.toString(),
        radius: radius.toString(),
        start_date: startDateTime,
        end_date: endDateTime,
      });
      console.log(params);
      
      const response = await fetch(
        `${API_BASE_URL}/search/?${params}`,
        {
          method: 'GET',
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const searchData = await response.json();
      setFacilities(searchData.facilities || []);
      setEvents(searchData.events || []);
      setResources(searchData.resources || []);
    } catch (err) {
      console.error('Error fetching nearby facilities:', err);
      setFacilities([]);
      setEvents([]);
      setResources([]);
    } finally {
      setFacilitiesLoading(false);
    }
  };

  const fetchPrimaryAddress = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/addresses/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const allAddresses = await response.json();
      setAddresses(allAddresses);
      
      // Filter for primary address
      const primary = allAddresses.find(addr => addr.is_primary === true);
      setPrimaryAddress(primary || null);
      
      // Set selected address to primary (or first address if no primary)
      const initialSelected = primary || (allAddresses.length > 0 ? allAddresses[0] : null);
      setSelectedAddress(initialSelected);
      
      let location = DEFAULT_CENTER;
      let searchRadius = 20;

      if (initialSelected && initialSelected.latitude && initialSelected.longitude) {
        location = {
          lat: parseFloat(initialSelected.latitude),
          lng: parseFloat(initialSelected.longitude),
        };
        setMapCenter(location);
        searchRadius = initialSelected.default_radius || 20;
        setRadius(searchRadius);
      } else {
        // No address with coordinates - use default center
        setMapCenter(DEFAULT_CENTER);
        location = DEFAULT_CENTER;
      }

      // Fetch nearby facilities with determined location and radius
      const startDateTime = combineDateAndTime(startDate, startTime);
      const endDateTime = combineDateAndTime(endDate, endTime);
      await fetchNearbyFacilities(location.lat, location.lng, searchRadius, startDateTime, endDateTime);
    } catch (err) {
      console.error('Error fetching addresses:', err);
      // On error, show empty map with default center
      setAddresses([]);
      setPrimaryAddress(null);
      setSelectedAddress(null);
      setMapCenter(DEFAULT_CENTER);
      // Still try to fetch facilities with default center
      const startDateTime = combineDateAndTime(startDate, startTime);
      const endDateTime = combineDateAndTime(endDate, endTime);
      await fetchNearbyFacilities(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng, 20, startDateTime, endDateTime);
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'absolute', inset: 0 }}>
        <GoogleMapsComponent
          primaryAddress={primaryAddress}
          selectedAddress={selectedAddress}
          facilities={facilities}
          loading={loading}
          mapCenter={mapCenter}
          facilitiesLoading={facilitiesLoading}
          isGoogleMapsLoaded={isGoogleMapsLoaded}
          googleMapsLoadError={googleMapsLoadError}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
          width: { xs: '95%', md: 600 },
          zIndex: 1,
        }}
      >
        <Card sx={{ width: '100%' }}>
          <Box
            onClick={() => setSearchExpanded((prev) => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              pt: 2,
              pb: searchExpanded ? 0 : 2,
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            <Typography variant="h6">Search for events</Typography>
            <IconButton
              size="small"
              aria-label={searchExpanded ? 'Collapse search' : 'Expand search'}
              aria-expanded={searchExpanded}
              onClick={(e) => {
                e.stopPropagation();
                setSearchExpanded((prev) => !prev);
              }}
            >
              <ExpandMore
                sx={{
                  transform: searchExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
              />
            </IconButton>
          </Box>
          <Collapse in={searchExpanded}>
            <SearchComponent
              showTitle={false}
              primaryAddress={primaryAddress}
              selectedAddress={selectedAddress}
              addresses={addresses}
              onAddressSelect={handleAddressSelect}
              onAddressChange={handleAddressChange}
              radius={radius}
              setRadius={setRadius}
              startDate={startDate}
              setStartDate={setStartDate}
              startTime={startTime}
              setStartTime={setStartTime}
              endDate={endDate}
              setEndDate={setEndDate}
              endTime={endTime}
              setEndTime={setEndTime}
              onSearch={handleSearchClick}
              isGoogleMapsLoaded={isGoogleMapsLoaded}
            />
          </Collapse>
        </Card>
      </Box>

      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: resultsExpanded ? {xs: '80%', md: '50%'} : {xs: 88, md: 88},
          zIndex: 2,
          backgroundColor: 'background.paper',
          overflow: 'visible',
          transition: 'height 0.3s ease',
        }}
      >
        <EventsList
          events={events}
          resources={resources}
          facilities={facilities}
          loading={facilitiesLoading}
          error={null}
          expanded={resultsExpanded}
          onTitleClick={() => setResultsExpanded((prev) => !prev)}
        />
      </Box>
    </Box>
  );
};

export default HomePage;

